import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRefreshVersions } from "./DataRefreshContext";

/**
 * Map server-pushed resource names to TanStack Query key prefixes.
 * SSE events fire with `resource: "bookings"` etc.; this maps them
 * to the query keys so `invalidateQueries` knows what to refetch.
 */
const RESOURCE_TO_QUERY_KEYS = {
  bookings: [["bookings"], ["dashboard"]],
  invoices: [["invoices"], ["bookings"], ["dashboard"]],
  enquiries: [["enquiries"], ["searches"], ["dashboard"]],
  properties: [["properties"]],
  suppliers: [["suppliers"]],
  quotes: [["quotes"], ["enquiries"]],
  options: [["options"], ["enquiries"], ["searches"]],
  communications: [["inbox"], ["communications"]],
  clients: [["clients"], ["dashboard"]],
  tickets: [["tickets"]],
  dashboard: [["dashboard"]],
};

/**
 * Hook that bridges DataRefreshContext version changes to TanStack Query
 * cache invalidation. Mount once in the authenticated layout.
 *
 * The single SSE connection lives in DataRefreshContext. This hook
 * watches the version counters it produces and invalidates the
 * corresponding TanStack Query keys when they change.
 */
export function useSSEInvalidation() {
  const queryClient = useQueryClient();
  const versions = useRefreshVersions();
  const prevVersionsRef = useRef({});

  useEffect(() => {
    const prev = prevVersionsRef.current;

    for (const resource of Object.keys(versions)) {
      if ((versions[resource] || 0) !== (prev[resource] || 0)) {
        const queryKeys = RESOURCE_TO_QUERY_KEYS[resource] || [[resource]];
        queryKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }
    }

    prevVersionsRef.current = { ...versions };
  }, [versions, queryClient]);
}
